import { databaseRef } from 'config/firebase'
// import ITmpAnswers from '../data/questionnaires/ITmpAnswers'
import { get, memoize } from 'lodash'
import IChild from 'types/database/children/child'
import IAbilities from 'types/database/children/child/abilities'
import IAssessments from 'types/database/children/child/assessments'
import IAssessment from 'types/database/children/child/assessments/assessment'
import { default as IAssessmentAbilities } from 'types/database/children/child/assessments/assessment/abilities'
import IUser, { IRole } from 'types/database/users/user'
import { notTrackedAbilities } from '../data/abitilies/childsAbilities'
import { gamesInCurrentTalentMap, gamesInTypes } from '../data/games/gameTypes'
import {
  baseFormAssessments,
  formTypeRefs,
  questionnairesInCurrentTalentMap
} from '../data/questionnaires/FormType'
import IScore from '../data/questionnaires/IScore'
import { childForms } from './questionnaires'

export const getPatientById = async (id: string) => {
  try {
    const patient = await databaseRef.child(`children/${id}`).once('value')
    return { ...patient.val(), patientId: patient.key }
  } catch (e) {
    return e
  }
}

export const getChildrenIds = (user: IUser) => {
  const role = user?.role === IRole.educator ? IRole.educator : IRole.parent
  switch (role) {
    case IRole.educator:
      return user.pupils ? Object.keys(user.pupils) : []
    case IRole.parent:
      return user.children ? Object.values(user.children) : []
    default:
      return []
  }
}

export const getParentsByIds = async (ids: string[]) => {
  try {
    const parentsRef = databaseRef.child('parents')
    const parents: any[] = []
    for (const parentId of ids) {
      const parent = await parentsRef.child(parentId).once('value')
      parents.push({ ...parent.val(), parentId })
    }
    return parents
  } catch (err) {
    throw err
  }
}

export const getCurrentAssessmentTimestamp = (
  child: IChild & { [key: string]: any }
): number | string => {
  const assessment = child.currentAssessment
    ? child.currentAssessment
    : child.assessments.relevant_assessment

  return assessment
}

export const getCurrentAssessment = (
  child: IChild & { [key: string]: any }
): IAssessment | undefined => {
  const assessment = getCurrentAssessmentTimestamp(child)

  if (!assessment) {
    return
  }

  return child.assessments[assessment]
}

export const getAssessmentKeysArr = memoize(
  (assessments: IAssessments): number[] => {
    const arr: number[] = []

    for (const key in assessments) {
      if (assessments.hasOwnProperty(key)) {
        const keyAsNumber = Number(key)

        if (!isNaN(keyAsNumber)) {
          arr.push(keyAsNumber)
        }
      }
    }

    return arr
  }
)

export const isBaseFormCompleted = child => {
  const assessment = getCurrentAssessment(child)

  let completed = true

  if (!assessment) {
    completed = false
  } else {
    for (const key of childForms(child)) {
      const score = get(assessment, formTypeRefs[key]) as IScore
      if (
        get(score, ['total_results', 'total_score', 'score'], undefined) ===
        undefined
      ) {
        completed = false
        break
      }
    }
  }

  return completed
}

export const isGamesDone = child => {
  const currentAssessment = getCurrentAssessment(child)
  if (currentAssessment) {
    const currentTalentMap = currentAssessment.current_talent_map
    for (const key in gamesInTypes) {
      if (gamesInTypes.hasOwnProperty(key)) {
        for (const gameName in gamesInTypes[key]) {
          if (gamesInTypes[key].hasOwnProperty(gameName)) {
            const element = gamesInTypes[key][gameName]
            for (const game of element) {
              const gameinTalentMap = gamesInCurrentTalentMap[key][game] || null
              if (
                !gameinTalentMap ||
                !currentTalentMap ||
                !(currentTalentMap[gameinTalentMap] > -1)
              ) {
                return false
              }
            }
          }
        }
      }
    }
  }
  return true
}

export const childDataCollected = child =>
  get(getCurrentAssessment(child), 'date_locked')

export const resultsReadyShown = (child: any): boolean => {
  const currentAssessment = getCurrentAssessment(child)
  return !!currentAssessment && !currentAssessment.results_ready_not_shown
}

export const listenChild = (
  id: string,
  onChange: (child: any) => void = () => {
    return
  },
  onError: (message: string) => void = () => {
    return
  }
) => {
  try {
    const ref = databaseRef.child('children').child(id)
    let f

    // **check if child exists for this user*/
    ref
      .child('birth_day')
      .once('value')
      // ** */
      .then(() => {
        f = ref.on(
          'value',
          snapshot =>
            snapshot && onChange({ ...snapshot.val(), patientId: snapshot.key })
        )
      })

      .catch(e => onError(e.message))

    return () => ref.off('value', f)
  } catch (e) {
    if (e?.message) {
      onError(e.message)
    }

    return () => {
      return
    }
  }
}

export const updUser = async userId => {
  try {
    await databaseRef
      .child('users')
      .child(userId)

      .child('parental_instructions_complete')
      .set(false)
  } catch (err) {
    console.error({ err })
  }
}

export const isFormsStarted = async (child, parentId: string = '') => {
  if (childDataCollected(child)) {
    return true
  }

  const assessment = getCurrentAssessment(child)

  const currentTalentmap = assessment?.current_talent_map

  if (currentTalentmap) {
    for (const key of baseFormAssessments) {
      const questionnarie = questionnairesInCurrentTalentMap[key]
      if (questionnarie.some(val => currentTalentmap[val] > -1)) {
        return true
      }
    }
  }
  // if (parentId) {
  //   const answers = (
  //     await databaseRef
  //       .child('answers_tmp')
  //       .orderByChild('childId')
  //       .equalTo(child.patientId)
  //       .once('value')
  //   ).val() as ITmpAnswers[]
  //   if (!answers) {
  //     return false
  //   }

  //   const filtered = Object.values(answers).filter(
  //     a =>
  //       a.parentId === parentId &&
  //       a.relevantId === get(child, ['assessments', 'relevant_assessment'])
  //   )
  //   if (filtered.length > 0) {
  //     return true
  //   }
  // }

  return false
}

export const getDefaultAbilities = (
  childData: any,
  value: number = 100
): IAbilities => {
  const notTracked = notTrackedAbilities(childData)

  const abilitiesUpdates = notTracked.reduce<IAbilities>(
    (acc, abilityKey) => ({ ...acc, [abilityKey]: value }),
    {}
  )

  return abilitiesUpdates
}

export const getDefaultAssessment = (
  childData: any,
  value: number = 100
): IAssessmentAbilities => {
  const notTracked = notTrackedAbilities(childData)

  const assessmentsUpdates = notTracked.reduce<IAssessmentAbilities>(
    (acc, abilityKey) => ({
      ...acc,
      [abilityKey]: { total_results: { total_score: { score: value } } }
    }),
    {}
  )

  return assessmentsUpdates
}

export default {
  getPatientById,
  getParentsByIds,
  getCurrentAssessment,
  isBaseFormCompleted,
  isGamesDone,
  resultsReadyShown,
  listenChild,
  childDataCollected,
  isFormsStarted,
  getDefaultAbilities,
  getDefaultAssessment
}



// WEBPACK FOOTER //
// ./src/services/patient.ts